import { parse as parseYaml, stringify as stringifyYaml } from "yaml"

const FRONTMATTER_BLOCK =
  /^---\s*\n([\s\S]*?)\n---\s*\n?([\s\S]*)$/

/** Opening YAML block only (for fold range); does not consume the rest of the file. */
const FRONTMATTER_OPEN_PREFIX = /^---\s*\n[\s\S]*?\n---\s*\n?/

export type ParsedMarkdown = {
  frontmatter: Record<string, unknown>
  body: string
  parseFailed: boolean
}

/**
 * Split a markdown file into YAML frontmatter and body (first --- block only).
 * Matches server-side parsing in scripts/routes/documents/file-operations.js.
 */
export function parseMarkdownWithFrontmatter(text: string): ParsedMarkdown {
  const trimmed = text ?? ""
  const match = trimmed.match(FRONTMATTER_BLOCK)
  if (!match) {
    return { frontmatter: {}, body: trimmed, parseFailed: false }
  }
  try {
    const parsed = parseYaml(match[1])
    const frontmatter =
      parsed && typeof parsed === "object" && !Array.isArray(parsed)
        ? (parsed as Record<string, unknown>)
        : {}
    return { frontmatter, body: match[2] ?? "", parseFailed: false }
  } catch {
    return { frontmatter: {}, body: trimmed, parseFailed: true }
  }
}

/**
 * Build markdown file text from structured frontmatter + body.
 */
export function stringifyMarkdownDocument(
  frontmatter: Record<string, unknown>,
  body: string
): string {
  const yamlStr = stringifyYaml(frontmatter, { lineWidth: 0 }).trimEnd()
  return `---\n${yamlStr}\n---\n\n${body}`
}

/**
 * API / legacy rows sometimes store the **full** markdown (YAML + body) in `content` while also
 * returning `frontmatter` JSON. The editor must keep `content` as **body only** in React state;
 * otherwise `stringifyMarkdownDocument(frontmatter, content)` prepends YAML twice.
 */
export function splitApiDocumentBodyAndFrontmatter(
  rawContent: string,
  apiFrontmatter: Record<string, unknown> | null | undefined
): { body: string; frontmatter: Record<string, unknown> } {
  const raw = rawContent ?? ""
  const apiFm = (apiFrontmatter ?? {}) as Record<string, unknown>
  const parsed = parseMarkdownWithFrontmatter(raw)

  if (!raw.trimStart().startsWith("---")) {
    return { body: raw, frontmatter: apiFm }
  }
  if (parsed.parseFailed) {
    return { body: raw, frontmatter: apiFm }
  }

  const fm =
    Object.keys(apiFm).length > 0
      ? apiFm
      : (parsed.frontmatter as Record<string, unknown>)
  return { body: parsed.body, frontmatter: fm }
}

/**
 * Full source text for the CodeMirror "Source" tab. Handles edge cases where `body` state still
 * embeds a leading `---` block (stale client or unusual API shape).
 */
export function assembleSourceMarkdownText(
  bodyState: string,
  frontmatterState: Record<string, unknown> | null | undefined
): string {
  const raw = bodyState ?? ""
  const fm = (frontmatterState ?? {}) as Record<string, unknown>
  const parsed = parseMarkdownWithFrontmatter(raw)
  const hasLeading = raw.trimStart().startsWith("---")

  if (hasLeading && parsed.parseFailed) {
    return raw
  }
  if (hasLeading && !parsed.parseFailed) {
    const eff =
      Object.keys(fm).length > 0
        ? fm
        : (parsed.frontmatter as Record<string, unknown>)
    return stringifyMarkdownDocument(eff, parsed.body)
  }
  return stringifyMarkdownDocument(fm, raw)
}

/**
 * Character range [from, to) of the first YAML frontmatter block at the start of the file
 * (through the closing `---` line and any trailing newline), for CodeMirror folding. Returns null
 * if there is no such block, so the whole document is body-only.
 */
export function getFrontmatterFoldRange(text: string): { from: number; to: number } | null {
  const t = text ?? ""
  const match = t.match(FRONTMATTER_OPEN_PREFIX)
  if (!match || match.index !== 0) {
    return null
  }
  return { from: 0, to: match[0].length }
}
